import axios from 'axios';

const PART_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 1000;
const DEFAULT_CONCURRENCY = 3;

// How many times the whole file is swept for parts S3 still has not got. A part
// that fails all its own attempts gets another chance in the next sweep, with a
// fresh signature and against a fresh answer about what has already landed.
const MAX_PASSES = 3;

// Matches MAX_PART_URLS_PER_REQUEST in the API.
const PART_URL_BATCH = 100;

// Above this a file is uploaded in parts. Below it the single PUT is fewer
// round trips, and that is what almost every photo takes.
export const MULTIPART_MINIMUM_FILE_BYTES = 16 * 1024 * 1024;

export const shouldUploadAsMultipart = (file) =>
  !!file && file.size >= MULTIPART_MINIMUM_FILE_BYTES;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryable = (error) => {
  if (axios.isCancel(error)) return false;
  // No response means the connection died mid-part, which is the ordinary case
  // on a site connection and the whole reason for uploading in parts.
  if (!error || !error.response) return true;
  const { status } = error.response;
  // A 403 here is almost always the part's own signature aging out during the
  // transfer, and the next attempt signs afresh. Other 4xx are real refusals.
  return status === 403 || !(status >= 400 && status < 500);
};

/**
 * Strips the Content-Type axios would otherwise default to.
 *
 * S3's UploadPart takes no content type, so a part URL is signed against an
 * empty one and sending any Content-Type at all fails the SigV2 check with
 * SignatureDoesNotMatch. axios defaults PUT bodies to
 * application/x-www-form-urlencoded, so without this every part 403s.
 *
 * The header buckets deleted here are per-request copies made by axios's config
 * merge, so this leaves axios.defaults alone for everything else.
 */
const stripContentType = [
  (data, headers) => {
    if (headers) {
      delete headers['Content-Type'];
      if (headers.put) delete headers.put['Content-Type'];
      if (headers.common) delete headers.common['Content-Type'];
    }
    return data;
  },
];

/**
 * Uploads one file to S3 in parts, resuming rather than restarting.
 *
 * The single whole-file PUT this replaces cannot survive an interruption: a
 * dropped connection three minutes into a video throws away all three minutes,
 * and one signature has to cover the entire transfer. Parts fix both. Each part
 * carries its own short-lived signature, and anything S3 has already accepted
 * is not sent again — the server is asked what it holds before every sweep, so
 * a part that made it stays made.
 *
 * `multipart` is the four API calls: create, getPartUrls, complete, abort.
 */
export const uploadFileByMultipart = async ({
  file,
  multipart,
  cancelToken,
  onUploadProgress,
  concurrency = DEFAULT_CONCURRENCY,
}) => {
  const cancelled = () => !!(cancelToken && cancelToken.reason);
  const throwIfCancelled = () => {
    if (cancelled()) throw new Error('Upload canceled');
  };

  throwIfCancelled();

  const { uploadId, partSize } = (await multipart.create()) || {};

  if (!uploadId || !partSize) {
    throw new Error('Multipart upload could not be started');
  }

  const partCount = Math.max(1, Math.ceil(file.size / partSize));
  const sizeOfPart = (partNumber) =>
    Math.min(partSize, file.size - (partNumber - 1) * partSize);

  // Bytes S3 has taken, per part. A part that fails goes back to zero rather
  // than leaving the bar showing progress that no longer exists.
  const loadedByPart = {};
  const done = new Set();

  const reportProgress = () => {
    if (!onUploadProgress) return;
    const loaded = Object.keys(loadedByPart).reduce(
      (total, partNumber) => total + loadedByPart[partNumber],
      0
    );
    onUploadProgress({ loaded: Math.min(loaded, file.size), total: file.size });
  };

  const markAlreadyUploaded = (uploadedParts) => {
    (uploadedParts || []).forEach(({ partNumber, size }) => {
      // Only trust a part whose size is what this file expects there. A part
      // left over from a different attempt at the same key would otherwise be
      // stitched into the object.
      if (size === sizeOfPart(partNumber)) {
        done.add(partNumber);
        loadedByPart[partNumber] = size;
      }
    });
    reportProgress();
  };

  const putPart = async (url, partNumber) => {
    const start = (partNumber - 1) * partSize;
    await axios.put(url, file.slice(start, start + sizeOfPart(partNumber)), {
      cancelToken,
      transformRequest: stripContentType,
      onUploadProgress: (progressEvent) => {
        loadedByPart[partNumber] = progressEvent.loaded;
        reportProgress();
      },
    });
    loadedByPart[partNumber] = sizeOfPart(partNumber);
    done.add(partNumber);
    reportProgress();
  };

  const uploadOnePart = async (partNumber, signedUrl) => {
    let url = signedUrl;
    let lastError;

    for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt++) {
      try {
        await putPart(url, partNumber);
        return;
      } catch (error) {
        loadedByPart[partNumber] = 0;
        reportProgress();

        if (axios.isCancel(error)) throw error;
        lastError = error;
        if (attempt === PART_ATTEMPTS || !isRetryable(error)) break;

        await wait(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));
        throwIfCancelled();

        // Sign afresh: the signature this attempt used may well have aged out
        // during the transfer that just failed.
        const refreshed = await multipart.getPartUrls({
          uploadId,
          partNumbers: [partNumber],
        });
        url = (refreshed && refreshed.urls && refreshed.urls[partNumber]) || url;
      }
    }

    throw lastError;
  };

  // A fixed pool rather than one promise per part: a 1 GB video is 128 parts,
  // and letting all of them contend would starve every one of them.
  const runPool = async (jobs) => {
    const queue = jobs.slice();
    const workers = Array.from(
      { length: Math.min(concurrency, queue.length) },
      async () => {
        while (queue.length) {
          throwIfCancelled();
          await queue.shift()();
        }
      }
    );
    await Promise.all(workers);
  };

  try {
    for (let pass = 1; pass <= MAX_PASSES; pass++) {
      throwIfCancelled();

      const outstanding = [];
      for (let partNumber = 1; partNumber <= partCount; partNumber++) {
        if (!done.has(partNumber)) outstanding.push(partNumber);
      }
      if (outstanding.length === 0) break;

      for (let i = 0; i < outstanding.length; i += PART_URL_BATCH) {
        throwIfCancelled();
        const batch = outstanding.slice(i, i + PART_URL_BATCH);
        const { urls, uploadedParts } = await multipart.getPartUrls({
          uploadId,
          partNumbers: batch,
        });

        markAlreadyUploaded(uploadedParts);

        await runPool(
          batch
            .filter((partNumber) => !done.has(partNumber))
            .map(
              (partNumber) => () => uploadOnePart(partNumber, urls[partNumber])
            )
        );
      }
    }

    if (done.size < partCount) {
      throw new Error(
        `Upload incomplete: ${done.size} of ${partCount} parts uploaded`
      );
    }

    throwIfCancelled();
    await multipart.complete({ uploadId });
  } catch (error) {
    // Leave nothing half-open. The bucket's lifecycle rule would sweep it in a
    // week anyway, so a failed abort must not replace the real error.
    try {
      await multipart.abort({ uploadId });
    } catch (abortError) {
      console.warn('Could not abort multipart upload', abortError);
    }

    if (axios.isCancel(error) || cancelled()) {
      throw new Error('Upload canceled');
    }
    throw error;
  }
};

export default uploadFileByMultipart;
